import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore'
import { db } from '../firebase'

// ============================================================================
// PUBLIC PROFILE SERVICE
// ============================================================================
// This service manages the publicTeacherProfiles collection, which contains
// ONLY the fields visible in "View as Student" mode. This collection has
// public read access so shared Teacher Page links work without authentication.
// ============================================================================

const DEBUG = true
const log = (msg: string, data?: any) => {
  if (DEBUG) console.log(`[PublicProfileService] ${msg}`, data || '')
}
const logError = (msg: string, err: any) => {
  if (DEBUG) console.error(`[PublicProfileService ERROR] ${msg}`, err)
}

// ============================================================================
// STRICT FIELD WHITELIST — only these fields are written to the public doc
// ============================================================================
const PUBLIC_FIELD_WHITELIST = [
  // Identity (public-safe)
  'firstName',
  'lastName',
  'displayName',
  'tagline',

  // Location (city/state only — no street, zip, country)
  'city',
  'state',
  'location',

  // Profile content
  'bio',
  'teachingPhilosophy',

  // Teaching info
  'instruments',
  'teachingLevels',
  'lessonFormats',
  'languages',
  'experience',
  'yearsExperience',

  // Pricing (public-safe subset)
  'pricing',

  // Media
  'profileImageUrl',
  'profilePictureUrl',
  'bannerImageUrl',
  'introVideos',

  // Credentials
  'credentials',
] as const

/**
 * Extract only whitelisted public fields from a teacher data object.
 * This is the ONLY function that determines what goes into the public collection.
 */
function extractPublicFields(
  teacherData: Record<string, any>,
): Record<string, any> {
  const publicData: Record<string, any> = {}

  for (const field of PUBLIC_FIELD_WHITELIST) {
    if (teacherData[field] !== undefined) {
      publicData[field] = teacherData[field]
    }
  }

  return publicData
}

// ============================================================================
// SYNC PUBLIC PROFILE
// ============================================================================
/**
 * Sync a sanitized copy of the teacher's profile to publicTeacherProfiles/{teacherId}.
 * Called after every successful updateTeacherProfile().
 *
 * Uses setDoc with merge:true so partial updates work correctly.
 * Failures are logged but do NOT block the primary teacher update.
 *
 * @param teacherId - The teacher's UID
 * @param teacherData - The full teacher data object (or partial update)
 */
export async function syncPublicProfile(
  teacherId: string,
  teacherData: Record<string, any>,
): Promise<void> {
  if (!teacherId) {
    logError('syncPublicProfile - missing teacherId', null)
    return
  }

  try {
    log('syncPublicProfile - START', { teacherId })

    const publicData = extractPublicFields(teacherData)

    // Only write if there are public fields to sync
    if (Object.keys(publicData).length === 0) {
      log('syncPublicProfile - no public fields in update, skipping')
      return
    }

    // Add metadata
    publicData.updatedAt = serverTimestamp()

    const publicRef = doc(db, 'publicTeacherProfiles', teacherId)
    await setDoc(publicRef, publicData, { merge: true })

    log('syncPublicProfile - SUCCESS', {
      teacherId,
      fieldCount: Object.keys(publicData).length,
    })
  } catch (error: any) {
    // Log but do NOT throw — public sync failure should not block primary updates
    logError('syncPublicProfile - FAILED (non-blocking)', error)
  }
}

/**
 * Perform a full sync of all public fields from the teacher's private document.
 * Useful for initial population or manual re-sync.
 *
 * @param teacherId - The teacher's UID
 */
export async function fullSyncPublicProfile(teacherId: string): Promise<void> {
  if (!teacherId) return

  try {
    log('fullSyncPublicProfile - START', { teacherId })

    // Read the full teacher document
    const teacherRef = doc(db, 'teachers', teacherId)
    const teacherSnap = await getDoc(teacherRef)

    if (!teacherSnap.exists()) {
      log('fullSyncPublicProfile - teacher doc not found', { teacherId })
      return
    }

    const teacherData = teacherSnap.data()
    const publicData = extractPublicFields(teacherData)
    publicData.updatedAt = serverTimestamp()

    const publicRef = doc(db, 'publicTeacherProfiles', teacherId)
    await setDoc(publicRef, publicData) // Full overwrite (not merge)

    log('fullSyncPublicProfile - SUCCESS', { teacherId })
  } catch (error: any) {
    logError('fullSyncPublicProfile - FAILED', error)
    throw error
  }
}

// ============================================================================
// READ PUBLIC PROFILE
// ============================================================================
/**
 * Get a public teacher profile by ID.
 * Reads from publicTeacherProfiles (publicly readable).
 *
 * @param teacherId - The teacher's UID
 * @returns The public profile data, or null if not found
 */
export async function getPublicTeacherProfile(
  teacherId: string,
): Promise<Record<string, any> | null> {
  if (!teacherId) return null

  try {
    log('getPublicTeacherProfile - START', { teacherId })

    const publicRef = doc(db, 'publicTeacherProfiles', teacherId)
    const publicSnap = await getDoc(publicRef)

    if (!publicSnap.exists()) {
      log('getPublicTeacherProfile - not found', { teacherId })
      return null
    }

    const data = publicSnap.data()
    log('getPublicTeacherProfile - SUCCESS', { teacherId })

    return {
      uid: publicSnap.id,
      ...data,
    }
  } catch (error: any) {
    logError('getPublicTeacherProfile - FAILED', error)
    throw error
  }
}

/**
 * Subscribe to real-time updates on a public teacher profile.
 *
 * @param teacherId - The teacher's UID
 * @param onData - Callback with profile data
 * @param onError - Callback on error
 * @returns Unsubscribe function
 */
export function subscribeToPublicProfile(
  teacherId: string,
  onData: (profile: Record<string, any> | null) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const publicRef = doc(db, 'publicTeacherProfiles', teacherId)

  return onSnapshot(
    publicRef,
    (snapshot) => {
      if (snapshot.exists()) {
        onData({
          uid: snapshot.id,
          ...snapshot.data(),
        })
      } else {
        onData(null)
      }
    },
    (error) => {
      logError('subscribeToPublicProfile - error', error)
      onError(error)
    },
  )
}
